import { useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  Add,
  Delete,
  DottedCircle,
  Laptop,
  LayersFront,
  Office,
  Pencil,
} from '@factorialco/f0-react/icons/app'
import {
  TrainingSessionsDocument,
  TrainingSessionsFiltersDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCreateSession } from 'modules/trainings/hooks/Sessions/useCreateSession'
import { useTrainingsDatatable } from 'modules/trainings/hooks/useTrainingsDatatable'
import { DEFAULT_DATA_COLLECTION_STORAGE } from 'modules/trainings/lib/collections'
import { formatSessionDate } from 'modules/trainings/lib/utils'

type Props = {
  trainingClassId: string
  trainingId: string
}
export const SessionsDataTable = ({ trainingClassId, trainingId }: Props) => {
  const { company } = useCurrentSession()
  const navigate = useNavigate()
  const client = useApolloClient()
  const isDuplicating = useRef(false)

  const { navigation } = useNavigation()
  const { getModalityTranslation } = useTrainingsDatatable()
  const getModalityIcon = (modality?: string | null) => {
    switch (modality) {
      case 'inperson':
        return Office
      case 'mixed':
        return DottedCircle
      case 'online':
        return Laptop
      default:
        return Office
    }
  }

  const { create } = useCreateSession({
    onCompleted(data) {
      if (data.trainingsMutations.createTrainingsSession?.errors.length) {
        return
      }
    },
  })

  const source = useGraphqlDataCollectionSource({
    document: TrainingSessionsDocument,
    mapData: (data) => data.trainings.sessionsConnection.nodes,
    itemUrl: (item) =>
      navigation.trainings.list.details_revamp.view.trainingClasses.detail.sessions.attendance.event.fullPath(
        { id: String(trainingId), classId: String(trainingClassId), eventId: item.id }
      ),
    search: {
      enabled: true,
      sync: true,
    },
    mapVariables: ({ filters, sortings, search }) => {
      if (filters && filters?.startsAt?.length) {
        track(trackingEvents.training.trainingSession.applyFilterStartDate)
      }
      if (filters && filters?.modality?.length) {
        const modalityFilter = filters?.modality.map((item) => {
          if (item === 'inperson') return 'onsite'
          else if (item === 'online') return 'virtual'
          else return 'hybrid'
        })
        track(trackingEvents.training.trainingSession.applyFilterModality, {
          selected_modalityfilter: modalityFilter,
        })
      }
      return {
        modality: filters.modality?.[0],
        startsAt: filters.startsAt ?? undefined,
        sort: sortings?.[0],
        search: search,
        trainingClassIds: [String(trainingClassId)],
      }
    },
    mapPagination: (data) => {
      return {
        total: data.trainings.sessionsConnection.totalCount ?? 0,
      }
    },
    filters: {
      modality: {
        type: 'in',
        label: i18n.t('trainings.sessions_data_collection.filters.modality.title'),
        options: {
          options: [
            {
              label: i18n.t('trainings.sessions_data_collection.filters.modality.onsite'),
              value: 'inperson',
            },
            {
              label: i18n.t('trainings.sessions_data_collection.filters.modality.virtual'),
              value: 'online',
            },
            {
              label: i18n.t('trainings.sessions_data_collection.filters.modality.hybrid'),
              value: 'mixed',
            },
          ],
        },
      },
      startsAt: {
        type: 'in',
        label: i18n.t('trainings.sessions_data_collection.filters.start_date'),
        options: {
          options: async () => {
            track(trackingEvents.training.trainingSession.filterStartDate)
            const { data } = await client.query({
              query: TrainingSessionsFiltersDocument,
              variables: {
                trainingClassIds: [String(trainingClassId)],
              },
              fetchPolicy: 'cache-first',
            })

            const startDatesArray = data.trainings.sessionsConnection.nodes.map((session) => {
              const startsAtLabel = session?.startsAt
                ? session.schedule === 'selfpaced'
                  ? dateToString(moment(session.startsAt), 'ui_dmy_long')
                  : i18n.t('trainings.sessions_data_collection.filters.start_date_label', {
                      startDate: dateToString(moment(session.startsAt), 'ui_dmy_long'),
                      startTime: dateToString(moment(session.startsAt), 'ui_time'),
                    })
                : '-'
              return {
                label: startsAtLabel,
                value: session.startsAt,
                schedule: session.schedule,
              }
            })
            const startDateSet = new Set()
            const uniqueDates: { label: string; value: string }[] = []
            startDatesArray.filter((item) => {
              const setItem = `${item.schedule}:${item.value}`
              !startDateSet.has(setItem) && item.value
                ? startDateSet.add(setItem) && uniqueDates.push(item)
                : null
            })
            return uniqueDates
          },
        },
      },
    },
    sortings: {
      name: {
        label: i18n.t('trainings.sessions_data_collection.name'),
      },
      startsAt: {
        label: i18n.t('trainings.sessions_data_collection.date'),
      },
    },
    defaultSortings: {
      field: 'startsAt',
      order: 'asc',
    },
    primaryActions: () => ({
      label: i18n.t('trainings.sessions_data_collection.new_session'),
      icon: Add,
      onClick: () => {
        track(trackingEvents.training.trainingSession.startNewSession)
        navigate(
          navigation.trainings.list.details_revamp.view.trainingClasses.detail.sessions.create.fullPath(
            { id: String(trainingId), classId: String(trainingClassId) }
          )
        )
      },
    }),
    itemActions: (item) => [
      {
        label: i18n.t('trainings.sessions_data_collection.edit'),
        icon: Pencil,
        onClick: () => {
          track(trackingEvents.training.trainingSession.editSessionStart)
          navigate(
            navigation.trainings.list.details_revamp.view.trainingClasses.detail.sessions.edit.fullPath(
              {
                id: String(trainingId),
                classId: String(trainingClassId),
                eventId: String(item.id),
              }
            )
          )
        },
      },
      {
        label: i18n.t('trainings.sessions_data_collection.duplicate'),
        icon: LayersFront,
        onClick: async () => {
          track(trackingEvents.training.trainingSession.duplicateSession)
          if (isDuplicating.current) return
          isDuplicating.current = true

          const name = i18n.t(
            'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.session_name_duplicate',
            { name: item.name }
          )
          const response = await create({
            ...item,
            trainingId: String(trainingId),
            trainingClassId: String(trainingClassId),
            subsidized: false,
            name: name,
            sendCalendarInvites: Boolean(item.calendarInvite),
            sessionInstructors: {
              employeeIds: [],
              sessionId: item.id.toString(),
              companyId: company.id,
            },
          })

          const handled = handleResult(response, ({}) => {
            app.setFlash({
              type: 'wrong',
              message: i18n.t('common.error'),
            })
            return
          })

          if (handled.success) {
            app.setFlash({
              type: 'correct',
              message: i18n.t('common.saved_successfully'),
            })
          }
          isDuplicating.current = false
        },
      },
      {
        label: i18n.t('trainings.sessions_data_collection.delete'),
        icon: Delete,
        critical: true,
        onClick: () => {
          navigate(
            navigation.trainings.list.details_revamp.view.trainingClasses.detail.sessions.delete.fullPath(
              {
                id: String(trainingId),
                classId: String(trainingClassId),
                eventId: String(item.id),
              }
            )
          )
        },
      },
    ],
  })

  return (
    <OneDataCollection
      id="trainings/sessions/v1"
      storage={DEFAULT_DATA_COLLECTION_STORAGE}
      source={source}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: [
              {
                label: i18n.t('trainings.sessions_data_collection.columns.session'),
                sorting: 'name',
                render: (item) => ({
                  type: 'text',
                  value: item.name,
                }),
              },
              {
                label: i18n.t('trainings.sessions_data_collection.columns.date'),
                sorting: 'startsAt',
                render: (item) => ({
                  type: 'text',
                  value:
                    item.startsAt || item.endsAt
                      ? formatSessionDate({
                          startsAt: item.startsAt ?? null,
                          endsAt: item.endsAt ?? null,
                          duration: item.duration,
                          schedule: item.schedule,
                        })
                      : '-',
                }),
              },
              {
                label: i18n.t('trainings.sessions_data_collection.columns.type'),
                render: ({ schedule }) => ({
                  type: 'dotTag',
                  value: {
                    label:
                      schedule === 'selfpaced'
                        ? i18n.t('trainings.sessions_data_collection.self_paced')
                        : i18n.t('trainings.sessions_data_collection.scheduled'),
                    color: schedule === 'selfpaced' ? 'malibu' : 'barbie',
                  },
                }),
              },
              {
                label: i18n.t('trainings.sessions_data_collection.columns.modality'),
                render: (item) => ({
                  type: 'tag',
                  value: {
                    label: getModalityTranslation(item.modality),
                    icon: getModalityIcon(item.modality),
                  },
                }),
              },
            ],
            frozenColumns: 1,
          },
        },
      ]}
    />
  )
}
